import { BoxContainer, Gauge, PrimaryButton, ReactIcons, StarRating } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "../WorkBox.module.css";

import surveyWorkBoxStyles from "./SurveyWorkBox.module.css";

import { isMeetingSessionRoleGreaterThanOrEqual, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { Loading } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionRole, Survey, SurveyStatus } from "@kalyzee/kast-web-sdk/websocket";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSdkContext } from "../../../contexts/sdk/context";
import { Card } from "../../common/Card";
import { Grid } from "../../common/Grid";
import { SurveyResultsViewer } from "../../survey/SurveyResultsViewer";
import { MainClockElapsedRenderer } from "../../renderers/mainClock";

export const SurveyViewerWorkBox = ({
  meetingAPI,
  survey,
  isAdmin,
}: {
  meetingAPI: MeetingContainerAPI;
  survey?: Survey;
  isAdmin?: boolean;
}) => {
  const [sessions] = useMeetingSelector(meetingAPI, "sessions");

  const [selectedAnswerIds, setSelectedAnswerIds] = useState<Set<string>>(new Set());

  const questionData = useMemo(() => {
    const question = survey?.questions[0];
    if (!question) return null;
    return {
      question,
      survey,
      minChoices: question.minChoiceNumber || 1,
      maxChoices: question.maxChoiceNumber || 1,
    }
  }, [survey]);

  const onlineUserIds = useMemo(() => {
    const ids = new Set<string>();
    sessions?.forEach((session) => {
        const id = session.user?.id;
        if (id && !isMeetingSessionRoleGreaterThanOrEqual(session.role, MeetingSessionRole.MODERATOR)) ids.add(id);
    });
    return ids;
  }, [sessions]);

  const surveyStats = useMemo(() => {
    const userIds = new Set<string>();
    survey?.questions.forEach((question) => {
      question.choices.forEach((choice) => {
        choice.votesUserIds?.forEach((userId) => {
          userIds.add(userId);
        });
      });
    });
    const onlineAnswers: Set<string> = new Set();
    const offlineAnswers: Set<string> = new Set();
    userIds.forEach((id) => {
      if (onlineUserIds.has(id)) onlineAnswers.add(id);
      else offlineAnswers.add(id);
    });
    return { onlineAnswers: onlineAnswers, offlineAnswers: offlineAnswers };
  }, [survey, onlineUserIds]);

  const selfAnswered = useMemo(() => {
    return questionData?.question.choices.some(choice => choice.selfVote);
  }, [questionData]);

  const sdkCtx = useSdkContext();

  useEffect(() => {
    setSelectedAnswerIds(new Set());
  }, [survey?.id]);

  const onPublishResults = useCallback(() => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !survey) return;
    client.emitter.survey.close({
      surveyId: survey.id,
    });
  }, [sdkCtx, survey]);

  const onDeleteSurvey = useCallback(() => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !survey) return;
    client.emitter.survey.remove({
      surveyId: survey.id,
    });
  }, [sdkCtx, survey]);

  const onAnswerSurvey = useCallback(async () => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !questionData) return;
    if (selectedAnswerIds.size < questionData.minChoices || selectedAnswerIds.size > questionData.maxChoices) return;
    const result = await client.emitter.survey.answerCreate({
      surveyId: questionData.survey.id,
      questions: [{
        id: questionData.question.id,
        choiceIds: Array.from(selectedAnswerIds),
      }]
    });
    if (result.error || !result.response) {
      console.error("Error answering survey: ", result.error);
      return;
    }
    meetingAPI.store.dispatch("surveyAnswer", { answer: result.response, isSelf: true });
  }, [sdkCtx.client, questionData, selectedAnswerIds, meetingAPI.store]);

  const onSelectChoice = useCallback((choiceId: string) => {
    const question = survey?.questions[0];
    if (!question) return;

    const maxChoice = question.maxChoiceNumber || 1;

    if (selectedAnswerIds.size >= maxChoice && maxChoice > 1 && !selectedAnswerIds.has(choiceId)) {
      // we don't know which choice to clear, so we do nothing
      return;
    }

    const newSelected = new Set(selectedAnswerIds);
    if (newSelected.has(choiceId)) {
      newSelected.delete(choiceId);
    } else {
      if (maxChoice === 1 && newSelected.size === 1) {
        // single choice, clear previous choice
        newSelected.clear();
      }
      newSelected.add(choiceId);
    }

    setSelectedAnswerIds(newSelected);
  }, [selectedAnswerIds, survey?.questions]);

  const renderChoicesString = (min: number, max: number, total: number) => {
    if (selfAnswered) {
      return "Vous avez soumis votre réponse.";
    }
    const minPlural = min > 1 ? 's' : '';
    if (min === max) {
      return `Choisissez ${min} option${minPlural}`;
    }
    if (max === total) {
      return `Choisissez au moins ${min} option${minPlural}`;
    }
    return `Choisissez entre ${min} et ${max} options`;
  }

  const renderChoiceOptions = () => {
    if (!questionData) return;

    if (questionData.survey.name === "Étoiles") {
      const choice = questionData.question.choices.find(choice => choice.selfVote || selectedAnswerIds.has(choice.id));
      let numericValue;
      try {
        numericValue = choice ? parseFloat(choice.content) : undefined;
      } catch (error) {
        console.error("Error parsing choice content to numeric value:", error);
        numericValue = undefined;
      }
      return (
        <StarRating
          maxStars={5}
          minStars={0.5}
          allowHalfStars
          showHoverPreview={!selfAnswered}
          value={numericValue}
          onChange={(choice) => {
            const selectedId = questionData.question.choices.find(c => c.content === choice.toString())?.id;
            if (selectedId) {
              onSelectChoice(selectedId);
            }
          }}
          starColors={{
            empty: "rgba(var(--color-purple-1-rgb), 0.25)",
            full: "var(--color-element-workbox-survey-star-fill)",
          }}
          starStyle={{
            width: "5em",
            height: "5em",
          }}
          containerClassName={clsx({
            [surveyWorkBoxStyles.disabledBtn]: selfAnswered,
          })}
        />
      )
    }
    return (
      <Grid
        elements={
          questionData.question.choices.map((choice) => {
            return (
              <Card.Radio 
                key={`choice-${choice.id}`}
                text={choice.content}
                checked={selfAnswered ? choice.selfVote : selectedAnswerIds.has(choice.id)}
                onClick={() => onSelectChoice(choice.id)}
                disabled={selfAnswered || (questionData.maxChoices > 1 && selectedAnswerIds.size >= questionData.maxChoices && !selectedAnswerIds.has(choice.id))}
              />
            );
          })
        }
      />
    );
  };

  const renderAnswerOptions = () => {
    if (!questionData) {
      return (
        <div>Le sondage ne contient pas de question.</div>
      );
    }
    if (questionData.question.choices.length === 0) {
      return (
        <div>Le sondage ne contient pas de choix.</div>
      );
    }
    return (
      <>
        <div className={surveyWorkBoxStyles.contentTitleBlock}>
          <div className={surveyWorkBoxStyles.contentTitle}>{questionData.question.content}</div>
          <div className={clsx(surveyWorkBoxStyles.textColor2, surveyWorkBoxStyles.contentSubTitle)}>
            {renderChoicesString(
              questionData.minChoices, 
              questionData.maxChoices, 
              questionData.question.choices.length,
            )}
          </div>
        </div>
        {renderChoiceOptions()}
        <PrimaryButton
          className={clsx({
            [surveyWorkBoxStyles.validateButton]: true,
            [surveyWorkBoxStyles.disabledBtn]: selfAnswered || selectedAnswerIds.size < questionData.minChoices,
          })}
          onClick={onAnswerSurvey}
        >
          {selfAnswered ? <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>En attente des résultats<Loading /></div> : "Valider ma réponse"}
        </PrimaryButton>
      </>
    );
  };

  const renderAnswerGauge = () => {
    return (
      <div className={surveyWorkBoxStyles.viewerStatsAnswers}>
        <div>
          <span className={surveyWorkBoxStyles.textColor1}>
            {surveyStats.onlineAnswers.size} réponse{surveyStats.onlineAnswers.size > 1 ? "s" : ""}
            </span>
          <span className={surveyWorkBoxStyles.textColor2}> 
            {` / ${onlineUserIds.size} participant${onlineUserIds.size > 1 ? "s" : ""} connecté${onlineUserIds.size > 1 ? "s" : ""}`}
          </span>
        </div>
        <Gauge
          direction="horizontal"
          fillValue={surveyStats.onlineAnswers.size}
          fillCapacity={onlineUserIds.size}
          classNameBg={surveyWorkBoxStyles.gaugeBg}
          classNameFill={surveyWorkBoxStyles.gaugeFill}
        />
        <div className={surveyWorkBoxStyles.textColor2}>
          {surveyStats.offlineAnswers.size > 0 ? `(+ ${surveyStats.offlineAnswers.size} participant${surveyStats.offlineAnswers.size > 1 ? "s" : ""} déconnecté${surveyStats.offlineAnswers.size > 1 ? "s" : ""})` : ""}
        </div>
      </div>
    );
  };

  const renderTimer = () => {
    if (!survey) return null;
    if (survey.status === SurveyStatus.Closed) {
      return (
        <div className={clsx(surveyWorkBoxStyles.timer, surveyWorkBoxStyles.closed)}>
          Résultats publiés
        </div>
      );
    }
    return (
      <MainClockElapsedRenderer
        since={survey.createdAt}
        render={elapsed => (
          <div className={surveyWorkBoxStyles.timer}>
            <ReactIcons.Timer />
            {elapsed}
          </div>
        )}
      />
    );
  };

  const renderCloseBtn = () => {
    if (!isAdmin) return null;
    const status = survey?.status;
    if (!status) return null;
    return (
      <PrimaryButton
        className={clsx({[surveyWorkBoxStyles.validateButton]: true, [surveyWorkBoxStyles.closed]: status === SurveyStatus.Closed})}
        text={status === SurveyStatus.Open ? "Publier les résultats" : "Mettre fin au sondage"}
        onClick={status === SurveyStatus.Open ? onPublishResults : onDeleteSurvey}
      />
    );
  };

  const renderSurveyResults = () => {
    if (!questionData) return null;
    return (
      <>
        <div style={{
          flex: 1,
          minHeight: 0,
          alignItems: "center",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}>
          <div className={surveyWorkBoxStyles.contentTitleBlock}>
            <div className={surveyWorkBoxStyles.contentTitle}>{questionData.question.content}</div>
          </div>
          {renderAnswerGauge()}
          <div style={{
            flex: 1,
            width: "100%",
            minHeight: 0,
          }}>
            <SurveyResultsViewer 
              survey={survey} 
              customColors={{
                normal: [{
                  background: "rgba(var(--color-purple-1-rgb), 0.25)",
                  fill: "var(--color-element-workbox-survey-star-fill)",
                  text: "var(--color-white)",
                  subText: "var(--color-element-workbox-survey-text-2)",
                }],
                stars: {
                  background: "rgba(var(--color-purple-1-rgb), 0.25)",
                  fill: "var(--color-element-workbox-survey-star-fill)",
                  text: "var(--color-element-workbox-survey-star-fill)",
                  subText: "var(--color-element-workbox-survey-star-fill)",
                }
              }}
            />
          </div>
        </div>
        {renderCloseBtn()}
      </>
    );
  }

  const renderContent = () => {
    if (!survey) {
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>Pas de sondage en cours.</div>
      );
    }
    if (!isAdmin && survey.status !== SurveyStatus.Closed) {
      return renderAnswerOptions();
    }
    return renderSurveyResults();
    
    //return renderOngoingStats();
  };

  return (
    <BoxContainer
      className={surveyWorkBoxStyles.container}
      topBarProps={{
        left: [],
        center: [
          <ReactIcons.ChatBubble style={{ width: 31 }} />,
          <span className={workBoxStyles.label}>Sondage</span>
        ],
        right: [],
      }}
    >
      <div className={surveyWorkBoxStyles.content}>
        {renderContent()}
        {renderTimer()}
      </div>
    </BoxContainer>
  );
}
